import type { ModuloVendible } from "@/lib/billing/precios"
import type { AreaDeModulo } from "@/lib/modules/areas"
import { DESCRIPCION_DE_AREA, MODULOS, NOMBRE_DE_AREA, SECTORES, type FichaDeModulo, type Sector } from "./catalogo"
import { PAGINAS_DE_SECTOR, type PaginaDeSector } from "./sectores"
import { MODULOS_EN } from "./en/modulos"
import { PAGINAS_DE_SECTOR_EN } from "./en/sectores"
import type { Idioma } from "./idioma"

/**
 * Los contenidos de la web pública por idioma. El castellano es la fuente; el
 * inglés (lib/landing/en) es su traducción, con las mismas claves.
 */

export function modulosDe(idioma: Idioma): FichaDeModulo[] {
  return idioma === "en" ? MODULOS_EN : MODULOS
}

export function fichaDe(idioma: Idioma, clave: ModuloVendible): FichaDeModulo {
  const f = modulosDe(idioma).find((m) => m.clave === clave)
  if (!f) throw new Error(`Sin ficha ${idioma} para ${clave}`)
  return f
}

export function fichaPorSlugDe(idioma: Idioma, slug: string): FichaDeModulo | undefined {
  return modulosDe(idioma).find((m) => m.slug === slug)
}

export function sectoresDe(idioma: Idioma): PaginaDeSector[] {
  return idioma === "en" ? PAGINAS_DE_SECTOR_EN : PAGINAS_DE_SECTOR
}

export function paginaDeSectorDe(idioma: Idioma, slug: string): PaginaDeSector | undefined {
  return sectoresDe(idioma).find((p) => p.slug === slug)
}

const AREA_EN: Record<AreaDeModulo, string> = { equipo: "Team", operaciones: "Operations", ventas: "Sales", direccion: "Management" }
const DESCRIPCION_EN: Record<AreaDeModulo, string> = {
  equipo: "Time tracking, shifts, time off, payroll and everything about people.",
  operaciones: "Stock, purchasing, production, expenses and banking.",
  ventas: "Invoices, till, table ordering and reservations.",
  direccion: "Reports, chat and projects: see and decide.",
}

export function nombreDeArea(idioma: Idioma, area: AreaDeModulo): string {
  return idioma === "en" ? AREA_EN[area] : NOMBRE_DE_AREA[area]
}

export function descripcionDeArea(idioma: Idioma, area: AreaDeModulo): string {
  return idioma === "en" ? DESCRIPCION_EN[area] : DESCRIPCION_DE_AREA[area]
}

const SECTORES_EN: Record<string, { nombre: string; descripcion: string }> = {
  hosteleria: { nombre: "Hospitality", descripcion: "Restaurants, bars, cafés, hotels" },
  tienda: { nombre: "Retail", descripcion: "Shops with premises" },
  ecommerce: { nombre: "Ecommerce", descripcion: "Online selling" },
  servicios: { nombre: "Services", descripcion: "Agencies, advisers, consultancies" },
  saas: { nombre: "Software", descripcion: "Subscription software" },
  otros: { nombre: "Other", descripcion: "Any other business" },
}

/** Los tipos de negocio del alta, para el configurador. */
export function tiposDeNegocio(idioma: Idioma): Sector[] {
  return idioma === "en" ? SECTORES.map((s) => ({ ...s, ...SECTORES_EN[s.clave] })) : SECTORES
}

/** La misma página de sector en el otro idioma (van en el mismo orden). */
export function slugDeSectorEn(destino: Idioma, slug: string): string | undefined {
  const origen: Idioma = destino === "es" ? "en" : "es"
  const i = sectoresDe(origen).findIndex((p) => p.slug === slug)
  return i < 0 ? undefined : sectoresDe(destino)[i]?.slug
}
